import { useGetList } from "ra-core";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, LeadSource } from "../types";

const FUNNEL_STEPS = [
  "new",
  "contacted",
  "appointment-set",
  "negotiation",
  "won",
] as const;

const FUNNEL_COLORS = ["#84cc16", "#16a34a", "#0d9488", "#2563eb", "#1e3a8a"];
const SOURCE_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#ea580c",
  "#16a34a",
  "#0891b2",
  "#ca8a04",
  "#e11d48",
  "#64748b",
];

const percent = (part: number, whole: number) =>
  whole === 0 ? 0 : Math.round((part / whole) * 100);

const reached = (contacts: Contact[], index: number) =>
  contacts.filter((contact) => {
    if (index === 0) return true;
    return (
      FUNNEL_STEPS.indexOf(
        (contact.stage ?? "") as (typeof FUNNEL_STEPS)[number],
      ) >= index
    );
  }).length;

export const StudioDashboard = () => {
  const { dealStages } = useConfigurationContext();
  const { data: contacts = [], isPending } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "id", order: "ASC" },
  });
  const { data: sources = [], isPending: sourcesPending } =
    useGetList<LeadSource>("lead_sources", {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "name", order: "ASC" },
    });

  if (isPending || sourcesPending) return null;

  return (
    <div className="mt-1 flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Funnel contacts={contacts} stages={dealStages} />
        <SourceDonut contacts={contacts} sources={sources} />
      </div>
      <KpiRings contacts={contacts} />
    </div>
  );
};

const Funnel = ({
  contacts,
  stages,
}: {
  contacts: Contact[];
  stages: { value: string; label: string }[];
}) => {
  const counts = FUNNEL_STEPS.map((_, index) => reached(contacts, index));
  const max = Math.max(...counts, 1);
  const won = contacts.filter((contact) => contact.stage === "won").length;
  const lost = contacts.filter((contact) => contact.stage === "lost").length;
  const conversion = contacts.length
    ? ((won / contacts.length) * 100).toFixed(1)
    : "0.0";
  const top = 240;
  const band = 48;
  const center = 150;

  return (
    <section className="rounded-xl border bg-card p-6">
      <h2 className="text-lg font-semibold">Lead funnel</h2>
      <p className="text-sm text-muted-foreground">
        Leads at this stage or further. Lost leads stay in the first step.
      </p>
      <svg
        viewBox="0 0 460 250"
        className="mx-auto mt-4 w-full max-w-lg"
        role="img"
        aria-label="Lead funnel"
      >
        {counts.map((count, index) => {
          const width = Math.max(64, (count / max) * top);
          const nextCount = counts[index + 1] ?? Math.round(count * 0.72);
          const nextWidth =
            index === counts.length - 1
              ? width * 0.72
              : Math.max(48, (nextCount / max) * top);
          const y = index * band;
          const x1 = center - width / 2;
          const x2 = center - nextWidth / 2;
          const label =
            stages.find((stage) => stage.value === FUNNEL_STEPS[index])
              ?.label ?? FUNNEL_STEPS[index];
          return (
            <g key={FUNNEL_STEPS[index]}>
              <polygon
                points={`${x1},${y} ${x1 + width},${y} ${x2 + nextWidth},${y + band} ${x2},${y + band}`}
                fill={FUNNEL_COLORS[index]}
              />
              <text
                x={center}
                y={y + 29}
                textAnchor="middle"
                fill="white"
                fontSize="16"
                fontWeight="700"
              >
                {count}
              </text>
              <text
                x={center + top / 2 + 16}
                y={y + 29}
                fontSize="13"
                fill="currentColor"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-center text-xl font-semibold">
        {conversion}% lead conversion rate
      </p>
      <p className="text-center text-sm text-muted-foreground">Lost: {lost}</p>
    </section>
  );
};

const KpiRings = ({ contacts }: { contacts: Contact[] }) => {
  const total = contacts.length;
  const contacted = contacts.filter((contact) =>
    ["contacted", "appointment-set", "negotiation", "won"].includes(
      contact.stage ?? "",
    ),
  ).length;
  const appointments = contacts.filter((contact) =>
    ["appointment-set", "negotiation", "won"].includes(contact.stage ?? ""),
  ).length;
  const won = contacts.filter((contact) => contact.stage === "won").length;

  return (
    <section className="rounded-xl border bg-card p-6">
      <h2 className="text-lg font-semibold">Key performance indicators</h2>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
        <Ring
          label="Contact rate"
          percent={percent(contacted, total)}
          count={contacted}
          caption="Contacted"
          color="#2563eb"
        />
        <Ring
          label="Appointment rate"
          percent={percent(appointments, total)}
          count={appointments}
          caption="Appointments"
          color="#ea580c"
        />
        <Ring
          label="Win rate"
          percent={percent(won, total)}
          count={won}
          caption="Won"
          color="#16a34a"
        />
      </div>
    </section>
  );
};

const Ring = ({
  label,
  percent: value,
  count,
  caption,
  color,
}: {
  label: string;
  percent: number;
  count: number;
  caption: string;
  color: string;
}) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const filled = (value / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <div className="text-4xl font-semibold">{value}%</div>
      <div className="mb-2 text-sm">{label}</div>
      <svg
        width="160"
        height="160"
        viewBox="0 0 160 160"
        role="img"
        aria-label={`${label} ${value}%`}
      >
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="16"
        />
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference - filled}`}
          transform="rotate(-90 80 80)"
        />
        <text
          x="80"
          y="78"
          textAnchor="middle"
          fontSize="28"
          fontWeight="700"
          fill="currentColor"
        >
          {count}
        </text>
        <text x="80" y="98" textAnchor="middle" fontSize="12" fill="#64748b">
          {caption}
        </text>
      </svg>
    </div>
  );
};

const SourceDonut = ({
  contacts,
  sources,
}: {
  contacts: Contact[];
  sources: LeadSource[];
}) => {
  const counts = new Map<string, number>();
  for (const contact of contacts) {
    const name =
      sources.find((source) => source.id === contact.source_id)?.name ??
      "No source";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const segments = [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  const total = contacts.length || 1;
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <section className="rounded-xl border bg-card p-6">
      <h2 className="text-lg font-semibold">Lead sources</h2>
      <div className="mt-4 flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:justify-center">
        <svg
          viewBox="0 0 220 220"
          className="h-56 w-56"
          role="img"
          aria-label="Lead sources"
        >
          {segments.map((segment, index) => {
            const length = (segment.count / total) * circumference;
            const circle = (
              <circle
                key={segment.name}
                cx="110"
                cy="110"
                r={radius}
                fill="none"
                stroke={SOURCE_COLORS[index % SOURCE_COLORS.length]}
                strokeWidth="28"
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 110 110)"
              />
            );
            offset += length;
            return circle;
          })}
          <circle cx="110" cy="110" r="48" fill="var(--card, #fff)" />
        </svg>
        <ul className="space-y-2 text-sm">
          {segments.map((segment, index) => (
            <li key={segment.name} className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{
                  backgroundColor: SOURCE_COLORS[index % SOURCE_COLORS.length],
                }}
              />
              <span>
                {segment.name}: {segment.count} (
                {((segment.count / total) * 100).toFixed(1)}%)
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
